/**
 * Cookie utilities for client-side token management
 * Compatible with Next.js middleware (server-side cookie reading)
 */

/**
 * Set a cookie
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Expiration in days (default: 7)
 */
export function setCookie(name: string, value: string, days: number = 7, domain?: string): void {
  if (typeof window === 'undefined') return;

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  const domainAttribute = domain ? `;domain=${domain}` : '';
  const secureAttribute = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${domainAttribute}${secureAttribute}`;
}

/**
 * Get a cookie value
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;

  const nameEQ = name + '=';
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }

  return null;
}

/**
 * Delete a cookie
 * @param name - Cookie name
 */
export function deleteCookie(name: string, domain?: string): void {
  if (typeof window === 'undefined') return;

  const domainAttribute = domain ? `;domain=${domain}` : '';
  const secureAttribute = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/${domainAttribute}${secureAttribute}`;
}

function getSharedCookieDomain(): string | undefined {
  return process.env.NEXT_PUBLIC_SHARED_COOKIE_DOMAIN;
}

/**
 * Gets the current access token, preferring the app cookie and falling back to
 * the token supplied by the host application in Local Storage.
 */
export function getCurrentToken(): string | null {
  const cookieToken = getCookie('current_token');
  if (cookieToken) return cookieToken;

  if (typeof window === 'undefined') return null;

  const localToken = window.localStorage.getItem('current_token');
  if (localToken) {
    // Keep middleware-compatible authentication for subsequent navigation.
    setCurrentToken(localToken, 7);
  }

  return localToken;
}

/** Stores the token in both supported browser stores. */
export function setCurrentToken(token: string, days: number = 7): void {
  const sharedCookieDomain = getSharedCookieDomain();
  if (sharedCookieDomain) {
    // Remove the legacy host-only cookie so there is only one token value.
    deleteCookie('current_token');
  }
  setCookie('current_token', token, days, sharedCookieDomain);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('current_token', token);
  }
}

/** Clears the token from both supported browser stores. */
export function deleteCurrentToken(): void {
  deleteCookie('current_token');
  const sharedCookieDomain = getSharedCookieDomain();
  if (sharedCookieDomain) {
    deleteCookie('current_token', sharedCookieDomain);
  }
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('current_token');
  }
}
